import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Container,
} from '@mui/material';
import {
  Plus,
  ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../components/common/Notification';
import { certificatesService } from '../../services/certificates';
import CustomButton from '../../components/ui/CustomButton';
import { CreateCertificateDialog } from './CertificateForms';

const CreateCertificatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { showNotification, NotificationComponent } = useNotification();
  const [, setIsLoading] = useState(false);
  const [createDialogOpen] = useState(true);

  const handleSave = useCallback(async (certificateData) => {
    setIsLoading(true);
    try {
      const savedCertificate = await certificatesService.createCertificate(certificateData);
      showNotification('Certificate created successfully!', 'success');
      
      // Navigate back to certificates page after a short delay
      setTimeout(() => {
        navigate('/certificates');
      }, 1500);
      
      return savedCertificate;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, showNotification]);

  const handleClose = useCallback(() => {
    navigate('/certificates');
  }, [navigate]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CustomButton
            variant="outlined"
            onClick={handleClose}
            startIcon={<ArrowLeft size={16} />}
            sx={{
              borderColor: isDarkMode ? '#475569' : '#d1d5db',
              color: isDarkMode ? '#e2e8f0' : '#374151',
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.05)',
              },
            }}
          >
            Back to Certificates
          </CustomButton>
        </Box>

        <Box
          sx={{
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.5) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(248, 250, 252, 0.5) 100%)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: 3,
            p: 4,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Plus size={28} color="#3b82f6" style={{ marginRight: 12 }} />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              Create New Certificate
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#cbd5e1' : '#64748b',
              mb: 1,
            }}
          >
            Fill out the form below to create a new certificate. All required fields are marked with an asterisk (*).
          </Typography>

          <Chip
            label="NEW CERTIFICATE"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
            }}
          />
        </Box>
      </Box>

      {/* Create Certificate Dialog */}
      <CreateCertificateDialog
        open={createDialogOpen}
        onClose={handleClose}
        onSave={handleSave}
        isDarkMode={isDarkMode}
      />

      {NotificationComponent}
    </Container>
  );
};

export default CreateCertificatePage;


